// Centralized subscription validation logic
export const subscriptionValidator = {
  /**
   * Check if a subscription is active
   * @param {string|Date} subscriptionEndDate - The subscription end date
   * @param {Date} currentDate - Optional current date for testing
   * @returns {Object} - { isActive: boolean, reason: string, daysRemaining: number }
   */
  isSubscriptionActive(subscriptionEndDate, currentDate = new Date()) {
    // Handle null/undefined subscription dates
    if (!subscriptionEndDate) {
      return {
        isActive: false,
        reason: 'no_subscription',
        daysRemaining: 0
      };
    }

    // Parse the subscription end date
    const endDate = subscriptionEndDate instanceof Date 
      ? subscriptionEndDate 
      : new Date(subscriptionEndDate);

    // Validate the date
    if (isNaN(endDate.getTime())) {
      console.error('Invalid subscription end date:', subscriptionEndDate);
      return {
        isActive: false,
        reason: 'invalid_date',
        daysRemaining: 0
      };
    }

    // Add 5-minute grace period to prevent race conditions during login
    const gracePeriodMs = 5 * 60 * 1000; // 5 minutes in milliseconds
    const currentDateWithGrace = new Date(currentDate.getTime() - gracePeriodMs);
    
    // Calculate time difference using the grace period
    const timeDiff = endDate.getTime() - currentDateWithGrace.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    // Use grace period adjusted time for comparison to prevent timing issues
    if (endDate < currentDateWithGrace) {
      return {
        isActive: false,
        reason: 'subscription_expired',
        daysRemaining: 0
      };
    }

    // Add warning for subscriptions expiring soon
    if (daysRemaining <= 7) {
      return {
        isActive: true,
        reason: 'expiring_soon',
        daysRemaining: daysRemaining
      };
    }

    return {
      isActive: true,
      reason: 'active',
      daysRemaining: daysRemaining
    };
  },

  /**
   * Log subscription check for debugging
   */
  logSubscriptionCheck(user, result) {
    // Enhanced logging for race condition debugging
    const logData = {
      user: user.username || user.email,
      subscription_end_date: user.subscription_end_date,
      result: result,
      current_time: new Date().toISOString(),
      current_time_ms: Date.now(),
      grace_period_applied: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    if (process.env.NODE_ENV === 'development') {
      console.group('Subscription Check (Enhanced)');
      console.table(logData);
      console.groupEnd();
    }
    
    // Always log subscription validation issues for debugging
    if (!result.isActive && result.reason === 'subscription_expired') {
      console.warn('Subscription expired during login - potential race condition:', logData);
    }
  },

  /**
   * Format subscription end date for display
   */
  formatEndDate(subscriptionEndDate) {
    if (!subscriptionEndDate) return 'No subscription';
    
    const endDate = new Date(subscriptionEndDate);
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    };
    
    return endDate.toLocaleDateString(undefined, options);
  }
};

export default subscriptionValidator;